/**
 * Delete View Confirmation Modal
 *
 * Shows a confirmation dialog before deleting a custom view.
 */

import { AlertTriangle } from 'lucide-react';

interface DeleteViewConfirmModalProps {
  viewName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting?: boolean;
}

export function DeleteViewConfirmModal({
  viewName,
  onConfirm,
  onCancel,
  isDeleting = false,
}: DeleteViewConfirmModalProps) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-surface-raised-light dark:bg-surface-raised-dark rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
              Delete View
            </h3>
            <p className="mt-2 text-sm text-text-secondary-light dark:text-text-secondary-dark">
              Are you sure you want to delete{' '}
              <strong className="text-text-primary-light dark:text-text-primary-dark">"{viewName}"</strong>? This
              action cannot be undone.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onCancel}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}
